import React, { useState, useEffect } from "react";
import {
  SafTextField,
  SafButton,
  SafContainer,
  SafText,
  SafTextArea,
  SafAlert,
  SafCheckbox,
  SafCheckboxGroup,
} from "@saffron/core-components/react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const getMiddlewareEndpoint = (operation) => {
  const baseEndpoint = process.env.REACT_APP_FUNCTIONAPP_ENDPOINT;
  // console.log("Endpoint:", baseEndpoint);
  if (!baseEndpoint) {
    console.error("Middleware endpoint is not defined.");
    return "";
  }
  return `${baseEndpoint}&operation=${operation}`;
};

const Snow = ({ userData, setUserData }) => {
  const [formData, setFormData] = useState({
    assetId: "",
    cloudApplicationOwners: "",
    cloudApplicationSuffix: "",
    mid: "",
  });

  const [selectedProviders, setSelectedProviders] = useState({
    AWS: false,
    AZURE: false,
    GCP: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [successMessages, setSuccessMessages] = useState([]);
  const [errorMessages, setErrorMessages] = useState([]);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [responseCode, setResponseCode] = useState(null);
  const [payload, setPayload] = useState({});
  const [userName, setUserName] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    if (userData && userData.firstName) {
      setUserName(userData.firstName);
    }
  }, [userData]);

  const callAPI = async (url, data) => {
    try {
      const response = await axios.post(url, data, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error:", error);
      return null;
    }
  };

  const isProviderSelected = Object.values(selectedProviders).some(
    (value) => value === true
  );

  const handleChange = (name, value) => {
    if (name === "cloudApplicationSuffix") {
      value = value.toLowerCase();

      if (value.length > 14) {
        setShowErrorAlert(true);
        setErrorMessages(["Maximum number of characters is 14"]);
      } else if (!/^[a-z0-9]*$/.test(value)) {
        setShowErrorAlert(true);
        setErrorMessages(["No symbols or spaces allowed"]);
      }
    }
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleProviderChange = (provider, isChecked) => {
    setSelectedProviders((prev) => ({
      ...prev,
      [provider]: isChecked,
    }));
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
  };

  const handleCloseLoadingAlert = () => {
    setIsLoading(false);
  };

  const handleSubmit = async (event) => {
  event.preventDefault();
  setAttemptedSubmit(true);
  if (!isProviderSelected) {
    setShowErrorAlert(true);
    setErrorMessages(["Please select at least one cloud provider"]);
    return;
  }
  // Reset messages and alert states
  setSuccessMessages([]);
  setErrorMessages([]);
  setShowSuccessAlert(false);
  setShowErrorAlert(false);
  // Show loading toast
  setIsLoading(true);
  // const startTime = performance.now();
  let cloudValue = "";
  const selectedCount = Object.values(selectedProviders).filter(Boolean).length;
  if (selectedCount === 3) {
    cloudValue = "all";
  } else {
    const selectedClouds = [];
    if (selectedProviders.AWS) selectedClouds.push("aws");
    if (selectedProviders.AZURE) selectedClouds.push("azure");
    if (selectedProviders.GCP) selectedClouds.push("gcp");
    cloudValue = selectedClouds.join(",");
  }
  // Function to clean comma-separated values by removing spaces around commas
  const cleanCommaSeparatedValues = (value) => {
    if (!value || typeof value !== 'string') return value;
    return value
      .split(',')
      .map(item => item.trim())
      .filter(item => item.length > 0) // Remove empty strings
      .join(',');
  };
  // Initialize request body with only the common fields
  const requestBody = {
    asset_id_num: formData.assetId,
    cloud: cloudValue
  };
  // Add GCP specific fields only if GCP is selected
  if (selectedProviders.GCP) {
    requestBody.gcp_cloud_app_owners = cleanCommaSeparatedValues(formData.cloudApplicationOwners);
    requestBody.gcp_cloud_app_suffix = formData.cloudApplicationSuffix;
  }
  // Add AZURE specific fields only if AZURE is selected
  if (selectedProviders.AZURE) {
    requestBody.mid_owners = cleanCommaSeparatedValues(formData.mid);
  }
  const operation = "insert";
  const endpoint = getMiddlewareEndpoint(operation);
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const result = await response.text();
      // console.log("response", result);

      // const endTime = performance.now();
      // const totalTime = endTime - startTime;
      // const totalTimeMinutes = totalTime / 60000; // Convert milliseconds to minutes
      // console.log(`Total time taken: ${totalTimeMinutes.toFixed(4)} minutes`);

      // Hide the loading toast
      setIsLoading(false);

      // Check if the response contains ticket information
      if (result.includes("ritm") || result.includes("status")) {
        try {
          // Convert Python-like dictionary string to valid JSON
          const jsonString = result
            .replace(/'/g, '"')  // Replace single quotes with double quotes
            .replace(/None/g, 'null'); // Replace Python None with JSON null
          
          const parsedResult = JSON.parse(jsonString);
          const successArray = [];
          const errorArray = [];
          
          // Format messages consistently for both success and failure
          Object.keys(parsedResult).forEach(provider => {
            const ticket = parsedResult[provider];
            const status = ticket.status || 'unknown';
            
            if (status === 'success') {
              // For success cases
              const ritmInfo = ticket.ritm || 'N/A';
              successArray.push(`${provider.toUpperCase()}: RITM '${ritmInfo}', Status: ${status}`);
            } else {
              // For failure cases
              const issue = ticket.issue || 'Unknown issue';
              errorArray.push(`${provider.toUpperCase()}: Status: ${status}, Issue: ${issue}`);
            }
          });
          
          // Store the messages
          setSuccessMessages(successArray);
          setErrorMessages(errorArray);
          
          // Set flags to show both alerts if needed
          if (successArray.length > 0) {
            setShowSuccessAlert(true);
          }
          if (errorArray.length > 0) {
            setShowErrorAlert(true);
          }
          
          setResponseCode(successArray.length > 0 ? 200 : 400);
          setPayload({
            status: successArray.length > 0 ? (errorArray.length > 0 ? "partial-success" : "success") : "error",
            successMessages: successArray,
            errorMessages: errorArray,
            code: successArray.length > 0 ? 200 : 400,
            details: parsedResult
          });
        } catch (parseError) {
          console.error("Error parsing ticket response:", parseError);
          // If parsing fails but it looks like a response, show the raw response
          setShowErrorAlert(true);
          setErrorMessages(["Error parsing response: " + result]);
          setResponseCode(400);
          setPayload({
            status: "error",
            message: "Error parsing response",
            code: 400,
          });
        }
      } else if (result === "success") {
        setShowSuccessAlert(true);
        setSuccessMessages(["Form submitted successfully"]);
        setResponseCode(200);
        setPayload({
          status: "success",
          message: "Form submitted successfully",
          code: 200,
        });
      } else {
        setShowErrorAlert(true);
        setErrorMessages([result]);
        setResponseCode(400);
        setPayload({ 
          status: "error", 
          message: result, 
          code: 400 
        });
      }
    } catch (error) {
      // Hide the loading toast before showing the error
      setIsLoading(false);
      
      setShowErrorAlert(true);
      setErrorMessages(["Network error occurred"]);
      setResponseCode(500);
      setPayload({
        status: "error",
        message: "Network error occurred",
        code: 500,
      });
    }
  };

  const fieldStyle = {
    marginBottom: "20px",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontWeight: "bold",
  };
  
  const formContainerStyle = {
    height: "700px", // Set a fixed height (adjust as needed)
    overflowY: "auto", // Enable vertical scrolling
    padding: "10px",
    border: "2px solid #e0e0e0", // Optional: adds a subtle border
    borderRadius: "4px", // Optional: rounds the corners
  };

  return (
    <form onSubmit={handleSubmit}>
      {isLoading && (
        <div style={{ marginBottom: '10px' }}>
          <SafAlert
            appearance="informational"
            alertType="inline" // Changed from "toast" to "inline"
            iconAriaLabel="Loading"
            closeAriaLabel="Close"
            onClose={handleCloseLoadingAlert}
          >
            Please wait...
          </SafAlert>
        </div>
      )}
      
      {showSuccessAlert && successMessages.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <SafAlert
            appearance="success"
            alertType="inline" // Changed from "toast" to "inline"
            iconAriaLabel="Success"
            closeAriaLabel="Close"
            onClose={handleCloseSuccessAlert}
          >
            {successMessages.map((message, index) => (
              <React.Fragment key={`success-${index}`}>
                {index > 0 && <br />}
                {message}
              </React.Fragment>
            ))}
          </SafAlert>
        </div>
      )}
      
      {showErrorAlert && errorMessages.length > 0 && (
        <div style={{ marginBottom: '10px' }}>
          <SafAlert
            appearance="error"
            alertType="inline" // Changed from "toast" to "inline"
            iconAriaLabel="Error"
            closeAriaLabel="Close"
            onClose={handleCloseErrorAlert}
          >
            {errorMessages.map((message, index) => (
              <React.Fragment key={`error-${index}`}>
                {index > 0 && <br />}
                {message}
              </React.Fragment>
            ))}
          </SafAlert>
        </div>
      )}
        
      <SafContainer className="Form" style={formContainerStyle}>
        <div style={fieldStyle}>
          <SafText
            appearance="body-strong-md"
            as="label"
            htmlFor="assetId"
            style={labelStyle}
          >
            Select Cloud Provider *
          </SafText>
          <SafCheckboxGroup
            invalid={attemptedSubmit && !isProviderSelected}
            validationMessage="Please select at least one cloud provider"
            required
          >
              { <SafCheckbox
              checked={selectedProviders.AWS}
              onChange={(e) => handleProviderChange("AWS", e.target.checked)}
            >
              AWS
            </SafCheckbox> }

            <SafCheckbox
              checked={selectedProviders.AZURE}
              onChange={(e) => handleProviderChange("AZURE", e.target.checked)}
            >
              AZURE
            </SafCheckbox>

            <SafCheckbox
              checked={selectedProviders.GCP}
              onChange={(e) => handleProviderChange("GCP", e.target.checked)}
            >
              GCP
            </SafCheckbox>
          </SafCheckboxGroup>
        </div>

        {/* Render Asset ID field only once if any provider is selected */}
        {isProviderSelected && (
          <div style={fieldStyle}>
            <SafText
              appearance="body-strong-md"
              as="label"
              htmlFor="assetId"
              style={labelStyle}
            >
              Asset Id *
            </SafText>
            <SafTextField
              id="assetId"
              name="assetId"
              value={formData.assetId}
              onChange={(e) => handleChange("assetId", e.target.value)}
              placeholder="Enter Asset Id"
              fullWidth
              required
            />
          </div>
        )}

        {/* GCP specific fields */}
        {selectedProviders.GCP && (
          <>
            <div style={fieldStyle}>
              <SafText
                appearance="body-strong-md"
                as="label"
                htmlFor="cloudApplicationOwners"
                style={labelStyle}
              >
                Cloud Application Owners [GCP]
              </SafText>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                }}
              >
                <div style={{ padding: "2px 10px" }}>
                  <p style={{ color: "#808080" }}>
                  Please provide a minimum of two owners for this application. They must be individuals with EAD user accounts and email addresses within TR Office 365. The owners must also have a TR Managed Google user account if they wish to access the Google Cloud Platform console. Please note that application owners and application users are NOT granted access by default and should be explicitly added.
                  </p>
                </div>
              </div>
              <br></br>
              <SafTextField
                id="cloudApplicationOwners"
                name="cloudApplicationOwners"
                value={formData.cloudApplicationOwners}
                onChange={(e) =>
                  handleChange("cloudApplicationOwners", e.target.value)
                }
                placeholder="Enter cloud application owners employee IDs"
                fullWidth
                // required={selectedProviders.GCP}
              />
            </div>

            <div style={fieldStyle}>
              <SafText
                appearance="body-strong-md"
                as="label"
                htmlFor="cloudApplicationSuffix"
                style={labelStyle}
              >
                Cloud Application Suffix [GCP]
              </SafText>
              <div
                style={{
                  backgroundColor: "#f0f0f0",
                }}
              >
                <div style={{ padding: "2px 10px" }}>
                  <p style={{ color: "#808080" }}>
                    Please enter a meaningful name/suffix that will be included
                    in the application name and related AD groups. Max 14
                    characters, lowercase alphanumerics only, without symbols or
                    spaces. Provide a meaningful value that identifies your
                    application
                  </p>
                </div>
              </div>
              <br></br>

              <SafTextField
                appearance="body-strong-md"
                id="cloudApplicationSuffix"
                name="cloudApplicationSuffix"
                value={formData.cloudApplicationSuffix}
                onChange={(e) =>
                  handleChange("cloudApplicationSuffix", e.target.value)
                }
                placeholder="Enter cloud application suffix"
                fullWidth
                // required={selectedProviders.GCP}
              />
            </div>
          </>
        )}

        {/* AZURE specific fields */}
        {selectedProviders.AZURE && (
          <>
            <div style={fieldStyle}>
              <SafText
                appearance="body-strong-md"
                as="label"
                htmlFor="mid"
                style={labelStyle}
              >
                M ID(s) of Owner(s) [Azure]
              </SafText>
              <SafTextField
                id="mid"
                name="mid"
                value={formData.mid}
                onChange={(e) => handleChange("mid", e.target.value)}
                placeholder="Enter the M ID(s) of Owners"
                fullWidth
                // required={selectedProviders.AZURE}
              />
            </div>
          </>
        )}

        <div style={{ marginTop: "20px" }}>
          <SafButton type="submit" appearance="primary" disabled={isLoading}>
            Submit
          </SafButton>
        </div>
      </SafContainer>
    </form>
  );
};

export default Snow;
